import { FC, useMemo } from 'react';
import Highlighter from 'react-highlight-words';
import { useRouter } from 'next/router';

import PaginationC from 'src/components/Layout/Pagination';
import { Title } from 'src/components/Layout/Typography';
import { ARTICLE_BY_GROUP } from 'src/constants/services';
import text from 'src/constants/text';
import { useSearch } from 'src/hooks';
import useDiseaseList from 'src/hooks/Disease/useDisease';
import { useQueryParams } from 'src/hooks/useQueryParams';
import { IArticleByGroup, ISearchResultArticleByGroup } from 'src/interface/ArticleByGroup';
import { formatQueryParams } from 'src/utils/func';

import classes from './style.module.css';

const TabAlphabet: FC = () => {
	const router = useRouter();
	const tabQuery = router.query?.layout ? { layout: router.query.layout } : {};

	const { updateQueryParam } = useQueryParams();

	const { alphabet, page, search: keyword } = router.query;

	const activeAlphabet = !keyword ? alphabet || 'a' : '';

	const { data, meta } = useDiseaseList(!!page ? Number(page) : 1, activeAlphabet as string);

	const { data: searchResult, isLoading } = useSearch(
		ARTICLE_BY_GROUP.model.penyakit,
		decodeURIComponent(keyword as string),
	);

	const listDrug: IArticleByGroup[] = useMemo((): IArticleByGroup[] => {
		if (keyword) {
			if (alphabet) return data;
			return searchResult?.map((i: ISearchResultArticleByGroup) => ({
				slug: i.document.slug,
				url: i.document.url,
				title: i.document.title,
				uid: i.document.uid,
			}));
		}
		return data;
	}, [data, searchResult, alphabet, keyword]);

	const handleSearchByAlphabet = (val: string) => {
		updateQueryParam({
			alphabet: val,
		});
	};

	const Highlight = ({ children }: { children: any }) => (
		<strong className={classes['highlight-result']}>{children}</strong>
	);

	return (
		<>
			<Title level={2} text="Pencarian Penyakit Berdasarkan Abjad" className={classes.title} />

			<ul className={classes.list}>
				{text.ALPHABET.split('').map((a) => {
					return (
						<li
							key={a}
							className={a.toLowerCase() === activeAlphabet ? classes.activeItem : classes.listItem}
							onClick={() => handleSearchByAlphabet(a.toLowerCase())}
						>
							{a}
						</li>
					);
				})}
			</ul>

			<div className={classes.result}>
				{keyword && searchResult?.length > 0 && !isLoading && (
					<>
						<h3>Daftar Penyakit</h3>

						{listDrug?.length > 0 ? (
							<p>{`Hasil pencarian dari “${decodeURIComponent(router.query.search as string)}”`}</p>
						) : (
							<p>{`Tidak ada hasil pencarian penyakit dengan kata kunci "${decodeURIComponent(
								router.query.search as string,
							)}"`}</p>
						)}
					</>
				)}

				<ol className={classes.gridList}>
					{(listDrug ?? []).map((item: IArticleByGroup, i: number) => (
						<li key={i}>
							<a href={`/penyakit/${item.url}${formatQueryParams(tabQuery)}`}>
								<Highlighter
									activeClassName={classes['highlight-text']}
									searchWords={[decodeURIComponent(router.query.search as string)]}
									textToHighlight={item.title}
									highlightTag={Highlight}
								/>
							</a>
						</li>
					))}
				</ol>
			</div>
			{!!activeAlphabet && !keyword && (
				<div className={classes.outpagination}>
					<PaginationC
						className={classes.pagination}
						pageSize={50}
						current={page ? Number(page) : 1}
						total={meta?.pagination?.total_records || 1}
						totalPage={meta?.pagination?.total_page || 1}
						onChange={(val: number) => {
							updateQueryParam((prev) => ({
								alphabet: prev.alphabet || activeAlphabet,
								page: val.toString(),
							}));
						}}
					/>
				</div>
			)}
		</>
	);
};

export default TabAlphabet;
